import { FC, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';

import Page from 'modules/_shared/components/Pages/Page';
import Button from 'modules/_shared/components/Buttons';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import PageListHeader from 'modules/_shared/components/Headers/PageListHeader';

import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';

import menuTypes from 'constants/menuTypes';

import Breadcrumb, {
  BreadcrumbItem,
} from 'modules/_shared/components/Breadcrumb';
import { IncentiveOfferType } from 'modules/incentivesMarket/types';
import { useIncentiveOffersList } from './context/IncentiveOffersListContext';
import { IncentiveOffersSummaryCard, IncentiveOffersSection } from './components';

import '../../styles/IncentivesMarket.scss';

const BUTTON_VARIANT = false;

type IncentiveOffersListProps = {
  incentiveOfferType?: IncentiveOfferType;
};

const IncentiveOffersList: FC<IncentiveOffersListProps> = ({
  incentiveOfferType,
}) => {
  const { t } = useTranslate();
  const { data, actions } = useIncentiveOffersList();
  const location = useLocation();
  const beneficiaryQuery = new URLSearchParams(location.search).get('beneficiaryId');
  const pq = beneficiaryQuery ? `?beneficiaryId=${beneficiaryQuery}` : '';

  useGetMenu(menuTypes.NULL);

  const breadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('Dashboard'), to: '/' },
      { label: data.societyName, to: `/sociedad/${data.societyId}` },
      {
        label: t('IncentivesMarket'),
        to: `/mercado-de-incentivos/${data.societyId}${pq}`,
      },
      { label: t('IncentiveOffersList'), isActive: true },
    ],
    [t, data.societyName, data.societyId, pq]
  );

  const pageActions = [
    <Button key="sell-offer" onClick={actions.handleCreateSellOffer} disabled={!data.canCreateOffer}>
      <span>{t('MakeOffer')}</span>
      <em className="icon ni ni-arrow-long-right ml-1" />
    </Button>,
    <Button
      key="buy-sell"
      variant="light"
      onClick={actions.handleCreateBuyOffer}
      disabled
    >
      <span>{t('BuySell')}</span>
      <em className="icon ni ni-arrow-long-right ml-1 d-none d-sm-inline-block" />
    </Button>,
  ];

  const showSellIncentiveOffers =
    !incentiveOfferType || incentiveOfferType === 'SELL';

  const showBuyIncentiveOffers = !incentiveOfferType || incentiveOfferType === 'BUY';

  return (
    <Page id="trading-offers-list-page" className="pb-5">
      <div className="nk-block-head-content mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <PageListHeader
        subTitle={t('Offers')}
        description={t('IncentiveOffersDescription')}
        actions={pageActions}
      />

      <div className="nk-block">
        <div className="row g-gs">
          <div className="col-md-6">
            <IncentiveOffersSummaryCard
              type={IncentiveOfferType.SELL}
              summary={data.sellOffersSummary}
              societyId={data.societyId}
              isButtonVariant={BUTTON_VARIANT}
            />
          </div>

          <div className="col-md-6">
            <IncentiveOffersSummaryCard
              type={IncentiveOfferType.BUY}
              summary={data.buyOffersSummary}
              societyId={data.societyId}
              isButtonVariant={BUTTON_VARIANT}
            />
          </div>

          {/* <div className="col-md-6 col-lg-4">
            <TradingOperationsCard
              operations={data.beneficiaryOperations}
              activeCount={data.activeOperationsCount}
              totalCount={data.totalOperationsCount}
              societyId={data.societyId}
              isButtonVariant={BUTTON_VARIANT}
            />
          </div> */}
        </div>
      </div>

      {showSellIncentiveOffers && (
        <IncentiveOffersSection
          type={IncentiveOfferType.SELL}
          offers={data.sellOffers}
          sortBy={data.sellOffersSortBy}
          onSortChange={actions.handleSellOffersSortChange}
          onViewDetail={actions.handleViewOfferDetail}
          societyId={data.societyId}
        />
      )}

      {showBuyIncentiveOffers && (
        <IncentiveOffersSection
          type={IncentiveOfferType.BUY}
          offers={data.buyOffers}
          sortBy={data.buyOffersSortBy}
          onSortChange={actions.handleBuyOffersSortChange}
          onViewDetail={actions.handleViewOfferDetail}
          societyId={data.societyId}
        />
      )}

      {data.isLoading && <CustomLoading />}
    </Page>
  );
};

export default IncentiveOffersList;
